<template>
  <CardManagementInsight>
    <div class="main-body" slot="body">
      <ManagementInsightCard :score="scoreManagement" :insights="insights" :dataLoaded="dataLoaded" />
      <DataPerformanceScore :score="scoreManagement" :isHigher="isHigher" :upScore="upScore" :dataLoaded="dataLoaded" />
      <DataPercentAverages :isDataIn="(missingData.length > 0 && !isNaN(missingData)) || (dataChanges.length > 0 && !isNaN(dataChanges) && !(dataChanges == 0)) || (totalQueries.length > 0 && !isNaN(totalQueries) && !(totalQueries == 0))" style="width:100%">
        <RadialBarChart :isDataIn="missingData.length > 0 && !isNaN(missingData)" slot="content" title="Missing Data" :series="missingData" />
        <RadialBarChart :isDataIn="dataChanges.length > 0 && !isNaN(dataChanges) && !(dataChanges == 0)" slot="content" title="Data Changes" :series="dataChanges" />
        <RadialBarChart :isDataIn="totalQueries.length > 0 && !isNaN(totalQueries) && !(totalQueries == 0)" slot="content" title="Total Queries" :series="totalQueries" />
      </DataPercentAverages>
    </div>
  </CardManagementInsight>
</template>
<script>
import _ from "lodash";
import ManagementInsightCard from "@/components/containers/management-insight-card";
import CardManagementInsight from "@/components/containers/card-management-insight";
import DataPerformanceScore from "@/components/containers/data-performance-score";
import DataPercentAverages from "@/components/containers/data-percent-averages";
import RadialBarChart from "@/components/charts/apex/radial-bar-chart";
import { getMissingData, getTotalQuery, getTotalDataChange } from "@/services/visualization/data-management-insight";

export default {
  props: {
    countries: [Array, String],
    sites: [Array, String],
    participantsSelected: [Array, String],
    datasources: [Object, null],
    dataLoaded: { type: Boolean, default: false }
  },
  components: {
    CardManagementInsight,
    ManagementInsightCard,
    DataPerformanceScore,
    DataPercentAverages,
    RadialBarChart
  },
  data() {
    return {
      scoreManagement: 0,
      insights: "higher",
      missingData: [],
      dataChanges: [],
      totalQueries: [],
      isHigher: "",
      upScore: 0,
      lastWeek: 0,
      totalPoint: 0,
      lastTotalPoint: 0
    };
  },

  methods: {
    setScore(missingData, totalQueries, dataChanges) {
      const missing = missingData > 100 ? 0 : 100 - missingData;
      const total = totalQueries > 100 ? 0 : 100 - totalQueries;
      const changes = dataChanges > 100 ? 0 : 100 - dataChanges;
      return (missing + total + changes) / 3 / 10;
    },

    setLastScore(lastMiss, lastTotalQueries, lastDataChanges) {
      const miss = lastMiss > 100 ? 0 : 100 - lastMiss;
      const total = lastTotalQueries > 100 ? 0 : 100 - lastTotalQueries;
      const data = lastDataChanges > 100 ? 0 : 100 - lastDataChanges;
      return (miss + total + data) / 3 / 10;
    },

    setInsight(score, lastScore) {
      const s = Number(parseFloat(score).toFixed(1));
      const lS = Number(parseFloat(lastScore).toFixed(1));

      if (s === lS) {
        this.upScore = Number(0);
        this.isHigher = "";
        this.insights = "equal";
      } else {
        this.upScore = Number(parseFloat(s - lS).toFixed(1));

        if (this.upScore < 0) {
          this.isHigher = "no";
          this.insights = "lower";
        } else {
          this.isHigher = "yes";
          this.insights = "higher";
        }
      }
    },

    processData(r, c = [], s = []) {
      let miss = null;
      let tq = null;
      let dc = null;
      let lastMiss = 0;
      let lastDataChanges = 0;
      let lastTotalQueries = 0;

      if (r["overallCollectionMetricRaw"]) {
        miss = getMissingData(r["overallCollectionMetricRaw"], c, s);
      }

      if (!_.isEmpty(r["totalQuery"])) {
        tq = getTotalQuery(r["totalQuery"], c, s);
      }

      if (!_.isEmpty(r["totalDataChange"])) {
        dc = getTotalDataChange(r["totalDataChange"], c, s);
      }

      if (miss) {
        if (miss["currentMissing"] && !isNaN(miss["currentMissing"])) {
          this.missingData = [miss.currentMissing];
        } else {
          this.missingData = NaN;
        }

        lastMiss = miss.lastMissing;
        this.totalPoint = miss.totalPoint;
        this.lastTotalPoint = miss.totalLastPoint;
      }

      if (miss === null) {
        this.missingData = [];
      }

      if (tq) {
        if (tq["currentQuery"]) {
          const cq = Number((tq["currentQuery"] / this.totalPoint) * 100).toFixed(0);
          const lcq = Number((tq["lastQuery"] / this.lastTotalPoint) * 100).toFixed(0);

          this.totalQueries = [parseInt(cq || 0)];
          lastTotalQueries = parseInt(lcq || 0);
        } else {
          this.totalQueries = [Number(0)];
          lastTotalQueries = Number(0);
        }
      } else {
        this.totalQueries = [Number(0)];
        lastTotalQueries = Number(0);
      }

      if (dc) {
        if (typeof dc["currentDataChange"] === "number" && dc["currentDataChange"] >= 0) {
          const cq = Number((dc["currentDataChange"] / this.totalPoint) * 100).toFixed(0);
          const lcq = Number((dc["lastDataChange"] / this.lastTotalPoint) * 100).toFixed(0);

          this.dataChanges = [parseInt(cq || 0)];
          lastDataChanges = parseInt(lcq || 0);
        }
      } else {
        this.dataChanges = [Number(0)];
        lastDataChanges = Number(0);
      }

      // Set the score and last score
      let score = 0;
      let lastScore = 0;

      const acumulateScore = parseInt(this.missingData[0] || 0) + parseInt(this.totalQueries[0] || 0) + parseInt(this.dataChanges[0] || 0);

      if (acumulateScore <= 0) {
        score = -1;
        lastScore = -1;
      } else {
        score = this.setScore(parseInt(this.missingData[0] || 0), parseInt(this.totalQueries[0] || 0), parseInt(this.dataChanges[0] || 0));
        lastScore = this.setLastScore(parseInt(lastMiss || 0), parseInt(lastTotalQueries || 0), parseInt(lastDataChanges || 0));
      }

      this.scoreManagement = score != "Infinity" ? Number(score).toFixed(1) : 0.0;
      this.setInsight(score, lastScore);
    }
  },

  watch: {
    datasources: function(d) {
      this.processData(d, _.uniq(this.countries), _.uniq(this.sites));
    },
    participantsSelected: function(values) {
      if (values && Array.isArray(values) && values.length === 0) {
        this.scoreManagement = -1;
      }
    },
    countries: function(c) {
      this.processData(this.datasources, _.uniq(c), _.uniq(this.sites));
    },

    sites: function(s) {
      this.processData(this.datasources, _.uniq(this.countries), _.uniq(s));
    }
  }
};
</script>
<style lang="scss" scoped>
.main-body {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: flex-start;
  justify-content: space-between;
}
</style>
